import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import clsx from 'clsx'
import { pick } from 'lodash-es'
import { Loading } from '@ow/comps'
import { useTbViewStore, useAppEx } from '@vt/hooks'
import { dayjsInit } from '@ow/utils'
import { fetchOrgData } from '@vt/apis/lib-ex/v2/orgs'
import useAmap from '../hooks/use-amap/use-user-polyline'
import LayerHandle from './layerHandle'
import DetailModal from './detailModal'
import Filter from './detailModal/filter'
import UserInfoPreview from './userInfoPreview'
import UserInfoModal from './userInfoPreview/mobileModal'
import UserMarker from './userMarker'
import { useInfoWindowPortal, useUserMarkerPortal } from './usePortal'
import { useUserStatusBySWR, useFootPrinterBySWR } from './useUserTrack'
import { useUserListFilter, useCustomizedCheck } from './config'
import { splitByDay, useMobileSize } from './utils'

const Map = () => {
  const { ditto } = useTbViewStore((state) => pick(state, ['ditto']))
  const { appId } = useAppEx((x) => pick(x, ['appId']))

  const isMobile = useMobileSize()

  const userFilter = useUserListFilter()
  const config = useCustomizedCheck()

  const [previewUser, setPreviewUser] = useState()
  const [selectedUser, setSelectedUser] = useState()
  const [curDate, setCurDate] = useState(
    config.dateRangePicker
      ? [dayjsInit().startOf('day').toDate(), new Date()]
      : new Date()
  )
  const [userList, setUserList] = useState([])
  const [filter, setFilter] = useState({})

  const userListWithPosition = useMemo(() => {
    return userList.filter((user) => user.position)
  }, [userList])

  const getUserStatusQueryTime = () => {
    if (Array.isArray(curDate)) {
      return {
        queryStartTime: curDate[0].valueOf(),
        queryEndTime: curDate[1].valueOf(),
      }
    }

    return { queryTime: dayjsInit(curDate).format('YYYY-MM-DD') }
  }

  const { userStatusData } = useUserStatusBySWR(
    ditto,
    {
      userId: userList.map((e) => e.id),
      ...getUserStatusQueryTime(),
    },
    3000
  )

  const { footPrinterData } = useFootPrinterBySWR(ditto, {
    userId: selectedUser?.id,
    createAt: curDate,
    ...filter,
  })

  const { amapState, polylinesLayer, usersLayer, inited } = useAmap(
    'viewmap',
    {}
  )

  const amap = amapState.getAmap()

  const { containerRef: infoWindowContainer, InfoWindow } =
    useInfoWindowPortal()
  const { containerRef: userMarkerContainers, Markers } = useUserMarkerPortal(
    userList.length
  )

  // 用ref存一下，避免对amap依赖
  const mapRef = useRef(amap)
  useEffect(() => {
    mapRef.current = amap
  }, [amap])

  // 关闭预览（mobile）
  const onPreviewClose = useCallback(() => {
    setPreviewUser(null)
  }, [])

  // 选中用户
  const onUserSelect = useCallback((user) => {
    mapRef.current?.clearMap()
    setPreviewUser(null)
    setSelectedUser(user)
  }, [])

  // 关闭详情
  const onDetailClose = useCallback(() => {
    setSelectedUser()
  }, [])

  const onTimeChange = useCallback((date) => {
    setCurDate(date)
    setSelectedUser()
  }, [])

  // 添加状态数据
  const dealUserStatus = useCallback((userStatusList) => {
    setUserList((pre) => {
      if (!pre.length || !userStatusList.length) return pre
      return pre.map((u, i) => {
        if (!userStatusList[i]) return u
        const { lng, lat } = userStatusList[i]
        const position = lng > 0 && lat > 0 ? [lng, lat] : null
        return {
          ...u,
          ...userStatusList[i],
          position,
        }
      })
    })
  }, [])

  // 点位绘制
  const userPointRender = useCallback((list, fitView = false) => {
    if (!usersLayer.current) return
    usersLayer.current.setUsers(
      list.map((user, i) => ({
        name: user.name,
        position: user.position,
        infoWindow: isMobile ? null : infoWindowContainer.current,
        userMarkerContent: userMarkerContainers.current[i],
        onMarkerClick: (address) => {
          setPreviewUser({ ...user, address })
        },
      }))
    )
    usersLayer.current.render()
    if (fitView) {
      mapRef.current?.setFitView()
    }
  }, [])

  // 轨迹绘制
  const userTrackRender = useCallback((list) => {
    if (!polylinesLayer.current) return

    polylinesLayer.current.setUserData(splitByDay(list))

    polylinesLayer.current.render()
    mapRef.current?.setFitView()
  }, [])

  // 获取用户列表
  useEffect(() => {
    const loadList = async () => {
      const orgs = await fetchOrgData(appId)
      const users = await ditto.fetchUsers()
      setUserList(userFilter(users, orgs))
    }
    loadList()
  }, [])

  // 更新用户状态
  useEffect(() => {
    if (!userStatusData) return
    dealUserStatus(userStatusData)
  }, [userStatusData])

  // userList渲染用户点位
  useEffect(() => {
    if (!inited || selectedUser) return
    mapRef.current?.clearMap()
    if (dayjsInit(curDate).isSame(dayjsInit(), 'day')) {
      userPointRender(userListWithPosition, true)
    }
  }, [userListWithPosition, selectedUser, curDate, inited])

  // footPrinterData渲染用户轨迹
  useEffect(() => {
    if (!selectedUser) return

    if (!footPrinterData?.length) {
      console.log('暂无数据')
      mapRef.current?.clearMap()
      return
    }

    userTrackRender(footPrinterData)
  }, [footPrinterData, selectedUser])

  return (
    <div className="w-full h-full flex">
      <LayerHandle
        userList={userList}
        curDate={curDate}
        selected={selectedUser}
        onTimeChange={onTimeChange}
        onSelect={onUserSelect}
        filter={filter}
        isMobile={isMobile}
      />
      <div
        className={clsx('h-full relative', !isMobile && 'p-4')}
        style={{ width: isMobile ? '100%' : 'calc(100% - 336px)' }}
      >
        <div id="viewmap" className="w-full h-full" />
        <DetailModal
          user={selectedUser}
          isMobile={isMobile}
          onClose={onDetailClose}
        />
        {isMobile && (
          <UserInfoModal
            user={previewUser}
            onClick={onUserSelect}
            onClose={onPreviewClose}
          />
        )}
        {config.filter && <Filter user={selectedUser} onChange={setFilter} />}
      </div>
      {!inited && (
        <div className="absolute h-screen w-screen flex justify-center items-center">
          <Loading />
        </div>
      )}
      <InfoWindow>
        <UserInfoPreview user={previewUser} onClick={onUserSelect} />
      </InfoWindow>
      <Markers>
        {userListWithPosition.map((user) => (
          <UserMarker key={user.id} user={user} />
        ))}
      </Markers>
    </div>
  )
}

export default memo(Map)
